'use client';

import { cn, PageContainer, SkipToContentLink, Slot, useSlots } from '@magicschool/spellbook';
import type React from 'react';
import { FormattedMessage } from 'react-intl';

const EmptyLayoutAlertsSlot = Slot<{ children?: React.ReactNode }>({
  displayName: 'EmptyLayout.Alerts',
  renderDefault: ({ children }) => <>{children}</>,
});

/**
 * Full-height shell with no navigation chrome. Provides a skip-to-content
 * link, an optional banner strip, and a `PageContainer` that fills the
 * viewport height (`h-dvh`).
 *
 * ### When to use
 * - Auth pages (sign-in, sign-up, confirm, etc.) via `(public)/(auth)/layout.tsx`
 * - Status and error pages (404, maintenance, global-error)
 * - Student join/login pages
 * - Any surface that needs a clean full-height canvas with no app nav chrome
 *
 * ### Composition (slots)
 * - `<EmptyLayout.Alerts>`: Renders its children verbatim above the
 *   `PageContainer`. Pass `<PageAlerts>` here so banners appear above the
 *   scroll container without affecting its height calculation.
 */
function EmptyLayoutBase({ children, className, style }: React.PropsWithChildren<{ className?: string; style?: React.CSSProperties }>) {
  const { slots, rest } = useSlots(children, {
    alerts: { Component: EmptyLayoutAlertsSlot },
  });

  return (
    <>
      <SkipToContentLink contentId="main-content">
        <FormattedMessage id="skip-to-content" />
      </SkipToContentLink>
      {slots.alerts}
      {/* @todo remove once all routes render a PageContainer (which will become <main id="main-content">) */}
      <main className="contents" id="main-content" tabIndex={-1}>
        <PageContainer className={cn('h-dvh', className)} style={style}>
          <PageContainer.Content>{rest}</PageContainer.Content>
        </PageContainer>
      </main>
    </>
  );
}

export const EmptyLayout = Object.assign(EmptyLayoutBase, {
  Alerts: EmptyLayoutAlertsSlot,
});
